import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, DOCUMENT, Inject, Injector, OnInit } from "@angular/core";
import { IdentityService } from "@common/identity/identity.service";
import { IMcpOAuthConsent } from "@common/identity/mcp-oauth-consent.interface";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";

const genericLoadError = "We couldn't load this access request. Return to the app and try connecting again.";

@Component({
    selector: "adapt-mcp-consent-page",
    templateUrl: "./mcp-consent-page.component.html",
    styleUrls: ["./mcp-consent-page.component.css"],
    standalone: false,
})
export class McpConsentPageComponent extends BaseRoutedComponent implements OnInit {
    public consent?: IMcpOAuthConsent;
    public error?: string;
    public submitting = false;

    public constructor(
        injector: Injector,
        private identityService: IdentityService,
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(DOCUMENT) private document: Document,
    ) {
        super(injector);
    }

    public async ngOnInit() {
        this.notifyActivated();
        this.shellUiService.removeDefaultShellPadding();
        const returnUrl = this.getSearchParameterValue("returnUrl");
        if (!returnUrl || !this.routeService.isMcpOAuthReturnUrl(returnUrl)) {
            this.error = "This access request is invalid. Return to the app and try connecting again.";
            return;
        }

        try {
            const response = await this.identityService.getMcpOAuthConsent(returnUrl);
            if (!response.body || typeof response.body.AppName !== "string" || !Array.isArray(response.body.Parameters)) {
                throw new Error("Invalid access request response");
            }
            this.consent = response.body;
        } catch (error) {
            this.error = getMcpConsentLoadError(error);
        } finally {
            this.changeDetectorRef.markForCheck();
        }
    }

    public submit(allow: boolean) {
        if (!this.consent || this.submitting) {
            return;
        }

        this.submitting = true;
        const form = this.document.createElement("form");
        form.method = "post";
        form.action = this.consent.FormAction;
        form.hidden = true;
        const parameters = [
            ...this.consent.Parameters,
            { Name: "consent_token", Value: this.consent.Token },
            { Name: "consent_decision", Value: allow ? "allow" : "deny" },
        ];
        for (const parameter of parameters) {
            const input = this.document.createElement("input");
            input.type = "hidden";
            input.name = parameter.Name;
            input.value = parameter.Value;
            form.appendChild(input);
        }

        this.document.body.appendChild(form);
        // Native navigation lets OpenIddict return the code (or access_denied) to the app.
        HTMLFormElement.prototype.submit.call(form);
        form.remove();
    }
}

function getMcpConsentLoadError(error: unknown) {
    // Only expose messages deliberately returned by the API; unexpected failures retain the generic guidance.
    return error instanceof HttpErrorResponse && typeof error.error?.Message === "string"
        ? error.error.Message
        : genericLoadError;
}
